import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Seo from '@/components/base/Seo';

const siteUrl =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) ||
  'https://biffdigital.com';
const pagePath = '/kvkk';
const pageUrl = `${siteUrl}${pagePath}`;

const pageSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      name: 'KVKK Aydınlatma Metni',
      description:
        'BIFF Digital KVKK Aydınlatma Metni. Kişisel verilerin nasıl işlendiği, veri sahibi hakları ve başvuru yöntemi hakkında bilgi.',
      url: pageUrl,
      inLanguage: 'tr',
      isPartOf: { '@type': 'WebSite', name: 'BIFF Digital', url: siteUrl },
      about: { '@type': 'Organization', name: 'BIFF Digital', url: siteUrl },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'KVKK Aydınlatma Metni', item: pageUrl },
      ],
    },
  ],
};

export default function Kvkk() {
  return (
    <div className="min-h-screen bg-background-50">
      <Seo
        title="KVKK Aydınlatma Metni | BIFF Digital"
        description="BIFF Digital KVKK Aydınlatma Metni. Kişisel verilerinizin nasıl işlendiğini, veri sahibi haklarınızı ve başvuru yöntemini öğrenin. 6698 sayılı Kişisel Verilerin Korunması Kanunu kapsamında."
        path={pagePath}
        type="website"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <Navbar />
      <main className="pt-24 md:pt-28 pb-16 md:pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-4">
            KVKK Aydınlatma Metni
          </h1>
          <p className="text-sm text-foreground-500 mb-8">
            Son güncelleme: 2 Eylül 2026
          </p>

          <div className="space-y-8 text-foreground-300 leading-relaxed">
            <p className="text-sm md:text-base">
              İşbu Aydınlatma Metni, 6698 sayılı Kişisel Verilerin Korunması Kanunu (&quot;KVKK&quot;) uyarınca,
              veri sorumlusu sıfatıyla hareket eden <strong>BIFF Digital</strong> tarafından hazırlanmıştır.
              Bu metin, kişisel verilerinizin hangi amaçlarla işlendiğini, kimlere ve hangi gerekçelerle
              aktarılabileceğini ve KVKK kapsamında sahip olduğunuz hakları açıklamak amacıyla sunulmaktadır.
            </p>

            <section>
              <h2 className="text-lg font-bold text-foreground-100 mb-3">1. Veri Sorumlusu</h2>
              <p className="text-sm md:text-base">
                KVKK uyarınca veri sorumlusu:
                <br /><br />
                <strong>BIFF Digital</strong>
                <br />
                Söğütözü Cad. No: 2 Koç Kuleleri C Blok 17, Çankaya/Ankara
                <br />
                E-posta: info@biffdigital.com
                <br />
                Telefon: +90 555 710 1968
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-foreground-100 mb-3">2. İşlenen Kişisel Veriler</h2>
              <div className="space-y-2 text-sm md:text-base">
                <p>Aşağıdaki kişisel veriler, aşağıda belirtilen yöntemlerle toplanmakta ve işlenmektedir:</p>
                <p><strong>Kimlik Bilgileri:</strong> Ad, soyad.</p>
                <p><strong>İletişim Bilgileri:</strong> E-posta adresi, telefon numarası.</p>
                <p><strong>İşlem Güvenliği Bilgileri:</strong> IP adresi, çerez kayıtları, site kullanım verileri.</p>
                <p><strong>Mesaj İçerikleri:</strong> İletişim formu üzerinden paylaştığınız talep ve mesaj metinleri.</p>
              </div>
            </section>

            <section>
              <h2 className="text-lg font-bold text-foreground-100 mb-3">3. Kişisel Verilerin İşlenme Amaçları</h2>
              <div className="space-y-2 text-sm md:text-base">
                <p>Toplanan kişisel veriler, aşağıdaki amaçlarla işlenmektedir:</p>
                <p>• İletişim ve talep formları aracılığıyla iletilen mesajların değerlendirilmesi ve yanıtlanması,</p>
                <p>• Hizmet tekliflerinin hazırlanması ve sözleşme süreçlerinin yürütülmesi,</p>
                <p>• Müşteri ilişkilerinin yönetilmesi ve memnuniyet süreçlerinin iyileştirilmesi,</p>
                <p>• Yasal yükümlülüklerin yerine getirilmesi ve yetkili mercilerin taleplerine cevap verilmesi,</p>
                <p>• Site trafiğinin analiz edilmesi ve kullanıcı deneyiminin iyileştirilmesi.</p>
              </div>
            </section>

            <section>
              <h2 className="text-lg font-bold text-foreground-100 mb-3">4. Toplanma Yöntemi ve Hukuki Sebebi</h2>
              <p className="text-sm md:text-base">
                Kişisel verileriniz; web sitemizdeki iletişim formu, e-posta, telefon ve çerezler aracılığıyla,
                elektronik ortamda toplanmaktadır. Kişisel verileriniz KVKK&apos;nın 5. maddesinde belirtilen
                &quot;bir sözleşmenin kurulması veya ifasıyla doğrudan ilgili olması&quot;, &quot;hukuki
                yükümlülüğün yerine getirilmesi&quot; ve &quot;meşru menfaat&quot; hukuki sebeplerine dayanılarak işlenmektedir.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-foreground-100 mb-3">5. Kişisel Verilerin Aktarılması</h2>
              <p className="text-sm md:text-base">
                Kişisel verileriniz; yukarıda belirtilen amaçların gerçekleştirilmesiyle sınırlı olarak,
                yasal yükümlülüklerin gerektirdiği durumlarda yetkili kamu kurum ve kuruluşları ile
                hizmet aldığımız teknoloji ve altyapı sağlayıcılarıyla paylaşılabilir. Verileriniz,
                ticari amaçlarla üçüncü kişilere satılmaz veya devredilmez.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-foreground-100 mb-3">6. Kişisel Verilerin Saklanma Süresi</h2>
              <p className="text-sm md:text-base">
                Kişisel verileriniz, işlenme amaçlarının gerektirdiği süre ve ilgili mevzuatta öngörülen
                zamanaşımı süreleri boyunca saklanır. Bu sürelerin sona ermesi veya işlenme amacının ortadan
                kalkması halinde verileriniz silinir, yok edilir veya anonim hale getirilir.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-foreground-100 mb-3">7. Veri Sahibinin Hakları</h2>
              <p className="text-sm md:text-base">
                KVKK&apos;nın 11. maddesi uyarınca herkes, veri sorumlusuna başvurarak aşağıdaki haklara sahiptir:
              </p>
              <div className="space-y-2 text-sm md:text-base mt-3">
                <p>• Kişisel verilerinin işlenip işlenmediğini öğrenme,</p>
                <p>• Kişisel verileri işlenmişse buna ilişkin bilgi talep etme,</p>
                <p>• İşlenme amacını ve amacına uygun kullanılıp kullanılmadığını öğrenme,</p>
                <p>• Yurt içinde veya yurt dışında aktarıldığı üçüncü kişileri bilme,</p>
                <p>• Eksik veya yanlış işlenmişse düzeltilmesini isteme,</p>
                <p>• KVKK&apos;nın 7. maddesinde öngörülen şartlar çerçevesinde silinmesini veya yok edilmesini isteme,</p>
                <p>• Düzeltme, silme veya yok etme işlemlerinin aktarıldığı üçüncü kişilere bildirilmesini isteme,</p>
                <p>• Münhasıran otomatik sistemler ile analiz edilmesi nedeniyle aleyhe bir sonucun ortaya çıkmasına itiraz etme,</p>
                <p>• Kanuna aykırı işlenmesi sebebiyle zarara uğraması hâlinde zararın giderilmesini talep etme.</p>
              </div>
            </section>

            <section>
              <h2 className="text-lg font-bold text-foreground-100 mb-3">8. Başvuru Yöntemi</h2>
              <p className="text-sm md:text-base">
                Yukarıda belirtilen haklarınız kapsamındaki taleplerinizi, kimliğinizi tespit edebilecek
                bilgilerle birlikte <strong>info@biffdigital.com</strong> adresine e-posta göndererek veya
                yukarıda belirtilen posta adresimize yazılı olarak iletebilirsiniz. Başvurularınız, talebin
                niteliğine göre en kısa sürede ve en geç 30 (otuz) gün içinde ücretsiz olarak sonuçlandırılır.
                Ancak işlemin ayrıca bir maliyet gerektirmesi hâlinde, Kişisel Verileri Koruma Kurulu&apos;nca
                belirlenen tarifedeki ücret talep edilebilir.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-foreground-100 mb-3">9. İletişim</h2>
              <p className="text-sm md:text-base">
                KVKK Aydınlatma Metni ile ilgili sorularınız için:
                <br /><br />
                <strong>BIFF Digital</strong>
                <br />
                Söğütözü Cad. No: 2 Koç Kuleleri C Blok 17, Çankaya/Ankara
                <br />
                E-posta: info@biffdigital.com
                <br />
                Telefon: +90 555 710 1968
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}